// Logique pure des écrans de l'urbanisme (lot L5-C2) : tons et libellés d'avancement, marqueurs et surfaces
// de la carte, texte des conditions de la décision. Testée seule ; le serveur reste seul juge des actes.
import { AVANCEMENTS_DEMANDE, SENS_AVIS_URBANISME, type AvancementDemande, type ConditionsDecision, type SensAvisUrbanisme, type UrbanismePublic } from '@cc/contrats';
import type { MarqueurCarte, TonEtat } from '@cc/ui';
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import type { CarteUrbanisme, ProprietesDemandeCarte, ProprietesZoneCarte } from './api';

export const TON_AVANCEMENT: Record<AvancementDemande, TonEtat> = { en_cours: 'attente', decidee: 'signer', terminee: 'ok' };
export const libelleAvancement = (a: AvancementDemande) => AVANCEMENTS_DEMANDE[a];
export const libelleSens = (s: SensAvisUrbanisme | null) => (s ? SENS_AVIS_URBANISME[s] : 'En attente');
export const TON_SENS: Record<SensAvisUrbanisme, TonEtat> = { favorable: 'ok', favorable_reserves: 'attente', defavorable: 'retard' };

/** Marqueurs des demandes localisées (numérotés comme la liste voisine). */
export function marqueursDemandes(c: CarteUrbanisme | undefined): MarqueurCarte[] {
  return (c?.features ?? [])
    .filter((f) => f.geometry.type === 'Point' && f.properties.objet === 'demande_urbanisme')
    .map((f, i) => {
      const p = f.properties as ProprietesDemandeCarte;
      const [longitude, latitude] = (f.geometry as unknown as { coordinates: [number, number] }).coordinates;
      return {
        id: p.id,
        numero: i + 1,
        longitude,
        latitude,
        libelle: `${p.numero ?? 'Sans numéro'} · ${p.type} · ${p.zone ? `zone ${p.zone}` : 'hors zone'}`,
        ton: p.avancement === 'en_cours' ? ('ouvert' as const) : ('autre' as const),
      };
    });
}

/** Emprises des zones (polygones), étiquetées par leur code. */
export function surfacesZones(c: CarteUrbanisme | undefined): FeatureCollection<Polygon | MultiPolygon> {
  return {
    type: 'FeatureCollection',
    features: (c?.features ?? [])
      .filter((f) => f.geometry.type === 'Polygon' && f.properties.objet === 'zone_urbanisme')
      .map((f) => ({ type: 'Feature' as const, id: f.id, geometry: f.geometry as Polygon, properties: { numero: (f.properties as ProprietesZoneCarte).code } })),
  };
}

/** Emprises publiques des zones en vigueur (portail). */
export function surfacesPubliques(u: UrbanismePublic | undefined): FeatureCollection<Polygon | MultiPolygon> {
  return { type: 'FeatureCollection', features: (u?.zones ?? []).map((z) => ({ type: 'Feature' as const, id: z.code, geometry: z.emprise as Polygon, properties: { numero: z.code } })) };
}

/** Ce qui manque encore avant la décision, en phrases ; liste vide quand l'autorité peut décider. */
export function texteConditions(c: ConditionsDecision): string[] {
  const t: string[] = [];
  if (c.avis_manquants.length > 0) t.push(`Avis requis manquants : ${c.avis_manquants.join(', ')}.`);
  if (c.inspection_manquante) t.push('Inspection exigée sans constat enregistré.');
  t.push(`Décision réservée à : ${c.autorite.libelle} (rôle ${c.autorite.role_code}).`);
  return t;
}

/** Emprise saisie en positions « longitude latitude », une par ligne ; anneau fermé automatiquement. */
export function texteVersPolygone(texte: string): { type: 'Polygon'; coordinates: [number, number][][] } | null {
  const points = texte.split(/\n+/).map((l) => l.trim()).filter(Boolean).map((l) => l.split(/[\s,;]+/).map(Number) as [number, number]);
  if (points.length < 3 || points.some((p) => p.length !== 2 || p.some((n) => !Number.isFinite(n)))) return null;
  const premier = points[0] as [number, number];
  const dernier = points[points.length - 1] as [number, number];
  const anneau = premier[0] === dernier[0] && premier[1] === dernier[1] ? points : [...points, premier];
  return anneau.length >= 4 ? { type: 'Polygon', coordinates: [anneau] } : null;
}
